import { FC } from "react";
import clsx from "clsx";
import { useTheme } from "@mui/material";
import { getIconPath } from "@/utils/helpers";
import { TechnicalSentiment } from "./technical-sentiment-section";
import { PivotPointsTable } from "./pivot-points-section";
import { MovingAveragesTable } from "./moving-averages-section";
import { TechnicalIndicatorsTable } from "./technical-indicators-section";
import { Card } from "../../shared/ui/card/Card";
import { TechnicalAnalysisData } from "../hooks/useSymbolTechnicalAnalysis";
import { formatDate } from "@/utils/dateTime";
import { AnalyticsLoader } from "../../shared/ui/loader/AnalyticsLoader";
import styles from "./TechnicalAnalysis.module.scss";
//import { ITechAnalytics } from "../types";
import { TimeFrames } from "../../types";

interface ITechnicalAnalysisViewProps {
    isActive: boolean;
    ticker: string;
    tickerLabel: string;
    data: TechnicalAnalysisData | null;
    isLoading: boolean;
    timeFrame: TimeFrames;
    setTimeFrame: (timeFrame: string) => void;
}

export const TechnicalAnalysisView: FC<ITechnicalAnalysisViewProps> = ({
    ticker,
    tickerLabel,
    data,
    isLoading,
    timeFrame,
    setTimeFrame,
}) => {
    const { palette } = useTheme();

    // const anaysisDate = data?.date;
    // based on TipRanks site https://www.tipranks.com/stocks/aapl/technical-analysis,
    // the analysis date is the current date. TODO: Need to clarify if this is correct.
    const analysisDate = formatDate(new Date(), "MMM D, YYYY hh:mm A");

    return (
        <div
            className={clsx(styles.technicalAnalysisSection, {
                [styles.isLoading]: isLoading,
            })}>
            {isLoading || !data ? (
                <AnalyticsLoader />
            ) : (
                <>
                    <header className={styles.analysisHeader}>
                        <h1 className={styles.analysisTitle}>
                            {tickerLabel} ({ticker}) Technical Analysis
                        </h1>
                        <div className={styles.poweredBy}>
                            <span>Powered by</span>
                            <img
                                src={getIconPath({
                                    name: `/analytics/tipRanks_${palette.mode}`,
                                    ext: "png",
                                })}
                                alt="TipRanks Logo"></img>
                        </div>
                    </header>
                    <main className={styles.analysisContent}>
                        <Card title="Technical Sentiment" withInfoIcon={false}>
                            <TechnicalSentiment
                                data={data}
                                timeFrame={timeFrame}
                                setTimeFrame={setTimeFrame}
                                ticker={ticker}
                            />
                        </Card>
                        <Card
                            withInfoIcon
                            title={`${tickerLabel} (${ticker}) Pivot Points`}
                            timestamp={formatDate(analysisDate, "MMM D, YYYY hh:mm A")}>
                            <PivotPointsTable data={data?.pivotPoints} />
                        </Card>
                        <div className={styles.tablesRow}>
                            <Card
                                withInfoIcon={false}
                                title={`${tickerLabel} (${ticker}) Moving Averages`}
                                timestamp={formatDate(analysisDate, "MMM D, YYYY hh:mm A")}>
                                <MovingAveragesTable data={data?.movingAverages} />
                            </Card>
                            <Card
                                withInfoIcon={false}
                                title={`${tickerLabel} (${ticker}) Technical Indicators`}
                                timestamp={formatDate(analysisDate, "MMM D, YYYY hh:mm A")}>
                                <TechnicalIndicatorsTable
                                    data={data?.indicators}
                                    ticker={ticker}
                                    tickerLabel={tickerLabel}
                                />
                            </Card>
                        </div>
                    </main>
                </>
            )}
        </div>
    );
};
